"use client";

import React, { createContext, useContext } from "react";

type AnalyticsContextType = {
    trackEvent: (eventName: string, params?: Record<string, unknown>) => void;
    trackPageView: (path?: string) => void;
    trackConversion: (label?: string, value?: number) => void;
};

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

function isTrackingAllowed(): boolean {
    if (typeof window === "undefined") return false;

    const cookieAccepted = document.cookie.includes("cc_analytics=true");
    const ccAccepted = window.CookieConsent?.acceptedCategory?.("analytics") === true;

    const allowed = typeof window.gtag === "function" && (cookieAccepted || ccAccepted);

    if (!allowed) {
        console.warn("[Analytics] Tracking blocked", {
            gtagLoaded: typeof window.gtag === "function",
            cookieAccepted,
            ccAccepted,
        });
    }

    return allowed;
}


export const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
    const trackEvent = (eventName: string, params: Record<string, unknown> = {}) => {
        if (isTrackingAllowed()) {
            window.gtag("event", eventName, params);
        } else {
            console.debug("[trackEvent] Skipped", { eventName, params });
        }
    };

    const trackPageView = (path?: string) => {
        if (isTrackingAllowed()) {
            window.gtag("event", "page_view", {
                page_path: path || window.location.pathname,
                page_title: document.title,
                language: navigator.language,
            });
        } else {
            console.debug("[trackPageView] Skipped");
        }
    };

    const trackConversion = (label?: string, value?: number) => {
        if (isTrackingAllowed()) {
            window.gtag("event", "conversion", {
                event_label: label,
                value: value,
                currency: "DKK",
            });
        } else {
            console.debug("[trackConversion] Skipped");
        }
    };

    return (
        <AnalyticsContext.Provider
            value={{
                trackEvent,
                trackPageView,
                trackConversion,
            }}
        >
            {children}
        </AnalyticsContext.Provider>
    );
};

export const useAnalytics = (): AnalyticsContextType => {
    const context = useContext(AnalyticsContext);
    if (!context) {
        throw new Error("useAnalytics must be used within an AnalyticsProvider");
    }
    return context;
};
